<template>
    <vx-modal :visible="props.visible" @update:visible="updateVisible" padding="standard" size="lg">
        <template v-slot:header>
            Send Test
            <small class="block text-sm text-slate-500 font-sans">
                Who should receive this test message?
            </small>
        </template>

        <div class="flex flex-col gap-4">
            <vx-tabs v-model="currentTab" size="lg">
                <vx-tab value="segment" data-test="tab-segment">Segment</vx-tab>
                <!-- TODO: org unit doesn't have contacts for now -->
                <vx-tab v-if="!props.orgUnitId" value="manual" data-test="tab-manual">Manual</vx-tab>
            </vx-tabs>

            <div v-if="currentTab === 'segment'">
                <select-segment
                    v-model="data.segment_id"
                    size="lg"
                    placeholder="Choose a Segment"
                    relativeDropdown
                    :orgUnitId="props.orgUnitId"
                    :groupId="props.groupId"
                    :clearable="false">
                </select-segment>
            </div>

            <!-- TODO: org unit doesn't have contacts for now -->
            <div v-if="currentTab === 'manual' && !props.orgUnitId">
                <vx-label size="lg">
                    Search contacts by phone number
                    <template v-slot:description>
                        Add up to {{ PHONE_NUMBERS_LIMIT }} phone numbers (comma separated).
                    </template>
                </vx-label>
                <contact-phone-number-selectable
                    v-model="data.contacts"
                    data-test="contacts-select"
                ></contact-phone-number-selectable>
            </div>

            <vx-alert color="info" :closeable="false">
                <template v-slot:icon>
                    <font-awesome-icon widthAuto :icon="faDiamondExclamation" />
                </template>
                Tests can only be sent {{ PHONE_NUMBERS_LIMIT }} numbers or less
            </vx-alert>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 justify-between lg:flex-row">
                <vx-button type="button" size="lg" color="muted" @click="updateVisible(false)"> Cancel </vx-button>
                <vx-button
                    :loading="sending"
                    :disabled="!data.contacts.length && !data.segment_id"
                    block
                    type="button"
                    size="lg"
                    color="primary"
                    class="grow"
                    @click="sendMessage"
                    data-test="test-send"
                >
                    Send
                    <font-awesome-icon widthAuto :icon="faFlaskVial"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { VxAlert, VxModal, VxButton, VxLabel, VxTabs, VxTab } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faDiamondExclamation, faFlaskVial } from '@fortawesome/pro-solid-svg-icons';
import { messagesClient } from '~/services';
import { catchRequestError } from '~/utils/request';
import ContactPhoneNumberSelectable from '~/components/message-hub/message-hub-new-conversation/ContactPhoneNumberSelectable.vue';
import { useToasted } from '~/composables/useToasted';
import SelectSegment from '~/components/general/SelectSegment.vue';
import { isNotEmpty } from '~/utils/string';

const props = defineProps({
    orgUnitId: {
        type: Number,
    },
    groupId: {
        type: String,
    },
    visible: {
        type: Boolean,
        required: true,
    },
    mediaUrl: {
        type: String,
        required: false,
    },
    mediaContentType: {
        type: String,
        required: false,
    },
    bodyMessage: {
        type: String,
        required: true,
    },
    snippetId: {
        type: Number,
        default: null,
    },
    snippetFieldBindings: {
        type: Array,
        default: () => ([]),
    },
    disableLockedSnippets: {
        type: Boolean,
        default: true,
    },
});

const emit = defineEmits(['update:visible']);

const toasted = useToasted();

const PHONE_NUMBERS_LIMIT = 20;
const teamId = Spark.state.currentTeam.id;
const isCorporate = computed(() => !props.orgUnitId && !props.groupId);
const sending = ref(false);
const currentTab = ref('segment');
const data = ref({
    contacts: [],
    segment_id: null,
});

const updateVisible = (visible) => {
    emit('update:visible', visible);

    if (!visible) {
        data.value.contacts = [];
        data.value.segment_id = null;
        currentTab.value = 'segment';
    }
};

watch(
    () => currentTab.value,
    (newTab) => {
        if (newTab === 'manual') {
            data.value.segment_id = null;
        } else if (newTab === 'segment') {
            data.value.contacts = [];
        }
    },
    {
        immediate: true,
    }
);

const sendMessage = async () => {
    if (sending.value) {
        return;
    }

    if (data.value.contacts.length > PHONE_NUMBERS_LIMIT) {
         toasted.show({
            title: 'Test Failed',
            description: `You can only select up to ${PHONE_NUMBERS_LIMIT} contacts`,
            type: 'error',
        });
        return;
    }

    try {
        sending.value = true;
        const message = {};

        if (!props.disableLockedSnippets && props.snippetId) {
            message.snippet_id = props.snippetId;
            message.field_bindings = (props.snippetFieldBindings || []).filter((b) => isNotEmpty(b.value));
        } else {
            message.body = props.bodyMessage;

            if (props.mediaUrl && props.mediaContentType) {
                message.media_url = props.mediaUrl;
                message.media_content_type = props.mediaContentType;
            }
        }

        const audience = {};
        if (currentTab.value === 'segment') {
            audience.segment_id = data.value.segment_id;
        } else {
            audience.contact_ids = data.value.contacts.map((contact) => contact.id);
        }

        const payload = {
            audience,
            messages: [
                {
                    body: 'This is a test message from Voxie.',
                },
                message,
            ],
        };

        if (isCorporate.value) {
            await messagesClient.sendTestMessages(teamId, payload);
        } else if (props.orgUnitId) {
            await messagesClient.orgUnitSendTestMessages(teamId, props.orgUnitId, payload);
        } else {
            await messagesClient.groupSendTestMessages(teamId, props.groupId, payload);
        }

        toasted.success('Test sent');
        emit('update:visible', false);
    } catch (e) {
        console.error(e);
        toasted.error(catchRequestError(e));
    } finally {
        sending.value = false;
    }
};
</script>
